import React, { useRef } from 'react'
import { Stack, TextField, useTheme } from '@mui/material'

import { authOtpBoxSx } from 'pages/onboarding/components/authFieldChrome'

/**
 * The per-digit code input — one box per digit, auto-advancing.
 *
 * ## Why this exists
 *
 * Platform had TWO hand-rolled copies of this (`SignInPage`'s OTP boxes and
 * `TwoFactorChallenge`'s 2FA boxes) with the digit state, auto-advance,
 * backspace-to-previous and paste handling duplicated near-identically — and the
 * settings modals (`EnrollTotpModal`, `EnrollSmsModal`) asked for the same 6-digit
 * code through a plain single text input instead, so the same task looked like two
 * different controls depending on where you met it. Adding boxes to the modals by
 * hand would have made four copies; §36 says extract on the SECOND.
 *
 * `TwoFactorChallenge` already claimed to have avoided a second hand-roll by sharing
 * `authOtpBoxSx`. That was true of the STYLING only — every behaviour was copied,
 * which is why the two drifted on the one behaviour that matters most (neither
 * auto-submits, and both drop a pasted "123-456" silently).
 *
 * ## Two behaviours the copies did not have
 *
 * **`onComplete`** fires when the last digit lands, by typing OR by paste — this is
 * the auto-submit hook. Deliberately NOT called on every render while full: it fires
 * on the transition to complete, so a failed submit that leaves the digits on screen
 * does not immediately resubmit itself. A user correcting one digit of a rejected
 * code would otherwise be resubmitting on every keystroke.
 *
 * **Tolerant paste.** The copies tested the clipboard against `^\d{6}$` after an
 * unconditional `preventDefault()`, so pasting `123-456`, `123 456` or
 * `Your code is 482910` did nothing at all and showed no reason why. Non-digits are
 * stripped first, matching what `apps/app`'s `OtpInput` already does.
 */
export interface CodeInputProps {
  value: string
  onChange: (value: string) => void
  /** Fired when the final digit lands — by typing or paste. The auto-submit hook. */
  onComplete?: (value: string) => void
  length?: number
  disabled?: boolean
  autoFocus?: boolean
  'aria-label'?: string
}

export const CodeInput = ({
  value,
  onChange,
  onComplete,
  length = 6,
  disabled,
  autoFocus,
  'aria-label': ariaLabel = 'Verification code',
}: CodeInputProps) => {
  const theme = useTheme()
  // `useRef` in a loop is not allowed, so the array is built once and indexed.
  const refs = useRef<Array<HTMLInputElement | null>>([])
  const digits = Array.from({ length }, (_, i) => value[i] ?? '')

  const commit = (next: string[]) => {
    const joined = next.join('')
    onChange(joined)
    // The transition to complete, not the state of being complete — see the
    // docblock on why that distinction stops a rejected code resubmitting itself.
    if (joined.length === length && next.every(Boolean) && value.length < length) {
      onComplete?.(joined)
    }
  }

  const setDigit = (index: number, raw: string) => {
    /*
      ⚠️ FILTER HERE, not only in `handleKeyDown`. That handler intercepts digit
      KEYSTROKES, but a letter is not intercepted — it falls through to `onChange`,
      and without this line it was stored and displayed. Observed live: "ssfsdd" sat
      in a six-box numeric code field.

      `inputMode` and `pattern` are keyboard HINTS to mobile, not validation, and
      `maxLength` limits length rather than character class. Neither rejects anything.
      Paste is filtered separately in `handlePaste`; this covers typing, IME input,
      autofill and drag-drop, which all arrive as a change event.
    */
    const digit = raw.replace(/\D/g, '').slice(-1)
    // A rejected character must not clear a box that already holds a digit — typing
    // a letter should be a no-op, not a deletion.
    if (!digit && raw) return
    const next = [...digits]
    next[index] = digit
    commit(next)
    if (digit && index < length - 1) refs.current[index + 1]?.focus()
  }

  const handleKeyDown = (index: number, e: React.KeyboardEvent<HTMLDivElement>) => {
    // Typing over a filled box replaces it rather than being swallowed by
    // `maxLength`, which is what makes correcting a single digit feel normal.
    if (/^\d$/.test(e.key)) {
      e.preventDefault()
      setDigit(index, e.key)
      return
    }
    if (e.key === 'Backspace' && !digits[index] && index > 0) {
      refs.current[index - 1]?.focus()
    }
  }

  const handlePaste = (e: React.ClipboardEvent<HTMLInputElement>) => {
    e.preventDefault()
    // Strip, don't reject — see the docblock. A code copied out of an email
    // routinely arrives with surrounding text or a separator.
    const pasted = e.clipboardData.getData('text/plain').replace(/\D/g, '').slice(0, length)
    if (!pasted) return
    const next = Array.from({ length }, (_, i) => pasted[i] ?? '')
    commit(next)
    refs.current[Math.min(pasted.length, length - 1)]?.focus()
  }

  return (
    // Geometry copied EXACTLY from the sign-in 2FA boxes, deliberately: `flex: 1 1 0`
    // + `minWidth: 0` so the row fills its container and the boxes stay square via
    // `authOtpBoxSx`'s `aspectRatio`, and `.MuiInputBase-root` (not
    // `.MuiOutlinedInput-root`) because that is the node the chrome helper is written
    // against. The whole point is that the settings modals now look like sign-in, so
    // the shared component adopts sign-in's appearance rather than inventing a third.
    <Stack
      direction="row"
      spacing={2}
      useFlexGap
      /*
        LEFT-aligned, matching the label above and the error below.
        
        This was centred when the boxes were capped, which looked fine in isolation
        and wrong in context: "Verification code" and its error text are left-aligned
        like every other field (§31), so a centred row of boxes floated away from its
        own label. Alignment follows the field, not the container.
      */
      justifyContent="flex-start"
      aria-label={ariaLabel}
    >
      {digits.map((digit, index) => (
        <TextField
          key={index}
          value={digit}
          disabled={disabled}
          autoFocus={autoFocus && index === 0}
          onChange={(e) => setDigit(index, e.target.value)}
          onKeyDown={(e) => handleKeyDown(index, e)}
          onPaste={handlePaste}
          inputRef={(el: HTMLInputElement | null) => {
            refs.current[index] = el
          }}
          inputProps={{
            maxLength: 1,
            inputMode: 'numeric',
            pattern: '[0-9]*',
            // Lets iOS/Android offer an SMS code above the keyboard.
            autoComplete: 'one-time-code',
            'aria-label': `${ariaLabel} digit ${index + 1}`,
            style: { textAlign: 'center', fontSize: '28px', lineHeight: 1.3, padding: 0 },
          }}
          sx={{
            flex: '1 1 0',
            minWidth: 0,
            /*
              ⚠️ THE CAP IS LOAD-BEARING, not cosmetic. `authOtpBoxSx` sets
              `aspectRatio: 1/1`, so with `flex: 1 1 0` alone a box's HEIGHT is driven
              by its container's WIDTH. That is fine in the ~396px sign-in column
              (~56px squares) and wrong everywhere else: in the 533px settings modal
              the same boxes inflate to ~74px, which is what "the input boxes are too
              big" was.

              56px matches what the sign-in column already renders, so that surface is
              unchanged and every other container now matches it instead of scaling
              past it.
            */
            maxWidth: 56,
            '& .MuiInputBase-root': authOtpBoxSx(theme, !!digit),
          }}
        />
      ))}
    </Stack>
  )
}
